import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { translate } from '@/services/TranslationService';
import { dispatch } from '@/services/store';
import { NovovilleButton, Typography } from '@/components/novoville';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/use-toast';
import { withNOVOHeader } from '../sideMenu/HeaderHOC';
import AmountInput from './AmountInput';
import PaymentDetailsCard from './PaymentDetailsCard';
import PaymentWebView from './PaymentWebView';
import PublicPaymentWebView from './PublicPaymentWebView';
import {
  getPaymentDetails,
  getPublicPaymentDetails,
  preparePayment,
} from './actions';

const PaymentConfirmation = ({ location, setHeaderTitle, ...props }) => {
  const isPublic =
    props.isPublic || location?.pathname?.startsWith('/public/payments');
  const paymentCode = React.useMemo(() => {
    const urlParams = location?.pathname?.split('/') || [];
    return urlParams[urlParams.length - 1] || '';
  }, [location?.pathname]);
  const prefetchedPaymentDetails = React.useMemo(
    () => location?.state?.paymentDetails,
    [location?.state?.paymentDetails]
  );

  useEffect(() => {
    const title = translate('sideMenu.Payments');
    setHeaderTitle(title);
  }, []);

  useEffect(() => {
    const fetchPaymentDetails = async () => {
      try {
        if (isPublic) {
          const hasPrefetchedDetails = !!prefetchedPaymentDetails;

          if (!hasPrefetchedDetails) {
            return props.history.push('/public/payments');
          }
          return;
        }

        if (!paymentCode) return;

        await dispatch(getPaymentDetails(paymentCode));
      } catch (e) {
        return props.history.push(isPublic ? '/public/payments' : '/payments');
      }
    };

    fetchPaymentDetails();
  }, [isPublic, paymentCode, prefetchedPaymentDetails, props.history]);

  const {
    paymentDetails: storePaymentDetails,
    loading: paymentDetailsLoading,
  } = useSelector((state) => state.paymentReducer) || {};
  const deeplinkAuthority = useSelector(
    (state) => state.deeplinkReducer?.authority
  );

  const locationPaymentDetails = location.state?.paymentDetails;

  const mergedPaymentDetails = {
    ...(locationPaymentDetails || {}),
    ...(storePaymentDetails || {}),
  };

  const {
    created_at,
    type,
    code,
    recipientName,
    invoiceUrl,
    paid,
    amount_in_cents,
    prompts,
    payment: currentPayment,
  } = mergedPaymentDetails || {};

  const [amount, setAmount] = useState(amount_in_cents || 0);
  const [paymentUri, setPaymentUri] = useState(null);
  const [publicPaymentId, setPublicPaymentId] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPreparingPayment, setIsPreparingPayment] = useState(false);
  const [slot, setSlot] = useState(prompts?.slot?.[0] || null);

  const hasPrompts = !!prompts && Object.keys(prompts).length > 0;
  const hasSlots =
    hasPrompts && Array.isArray(prompts.slot) && prompts.slot.length > 0;

  useEffect(() => {
    if (typeof amount_in_cents === 'number' && !Number.isNaN(amount_in_cents)) {
      setAmount(amount_in_cents);
    }
  }, [amount_in_cents]);

  useEffect(() => {
    if (Array.isArray(prompts?.slot) && prompts.slot.length > 0) {
      setSlot((prev) =>
        prev === null || !prompts.slot.includes(prev) ? prompts.slot[0] : prev
      );
    }
  }, [prompts]);

  const handlePreparePayment = async () => {
    setIsPreparingPayment(true);
    try {
      const paymentRecord = currentPayment;

      if (!paymentRecord?.id) {
        throw new Error('Payment not found');
      }

      const payload = {
        payment_id: paymentRecord.id,
        amount,
        isPublic,
      };

      if (slot) {
        payload.prompts = {
          slot,
        };
      }

      const uri = await dispatch(preparePayment(payload));

      if (isPublic) {
        setPublicPaymentId(paymentRecord.id);
      } else {
        setPaymentUri(uri || null);
      }

      const preparedAmount =
        typeof payload.amount === 'number' ? payload.amount : Number(amount);

      if (hasPrompts && !uri && preparedAmount === 0) {
        toast({
          title: translate('PaymentCompleted'),
          variant: 'success',
          duration: 5000,
        });
        props.history.push(
          isPublic ? '/public/payment/success' : '/applications'
        );
      }
    } catch (error) {
      setPaymentUri(null);
      setPublicPaymentId(null);
      toast({
        title: translate('OTHER.errorOcurred'),
        description: error.data?.result || translate('paymentPreparationError'),
        variant: 'destructive',
      });
    } finally {
      setIsPreparingPayment(false);
    }
  };

  if (paymentUri) {
    return (
      <PaymentWebView
        uri={paymentUri}
        onError={(error) => {
          setPaymentUri(null);
          toast({
            title: translate('OTHER.errorOcurred'),
            description: error,
            variant: 'destructive',
          });
        }}
        onSucccess={() => {
          setPaymentUri(null);
          toast({
            title: translate('PaymentCompleted'),
            variant: 'success',
            duration: 5000,
          });
          props.history.push(isPublic ? '/public/payment/success' : '/');
        }}
      />
    );
  }

  if (isPublic && publicPaymentId) {
    return (
      <PublicPaymentWebView
        authority={
          deeplinkAuthority || location?.state?.authority || prompts?.authority
        }
        paymentId={publicPaymentId}
        onError={(error) => {
          setPublicPaymentId(null);
          toast({
            title: translate('OTHER.errorOcurred'),
            description: error,
            variant: 'destructive',
          });
        }}
        onComplete={() => {
          toast({
            title: translate('PaymentCompleted'),
            variant: 'success',
            duration: 5000,
          });
          props.history.push('/public/payment/success');
        }}
      />
    );
  }
  if (!code) return;

  const isAmountLoading = paymentDetailsLoading && !isPreparingPayment;

  return (
    <div className="flex flex-1 sm:flex-grow-0 flex-col relative">
      <div className="flex flex-col sm:m-10 m-0 sm:max-h-[calc(100vh-10rem)] overflow-y-auto sm:py-8 py-2 sm:px-8 px-4 flex-1 h-fit bg-white rounded-sm">
        <div className="flex flex-1 flex-col *:mb-8 first:mb-6 max-w-md">
          <Typography variant="bold_20" className="text-gray-800 mb-4">
            {translate('PaymentConfirmation')}
          </Typography>
          <PaymentDetailsCard
            issueDate={created_at}
            type={type}
            rfCode={code}
            payerName={recipientName}
          />
          {isAmountLoading ? (
            <Skeleton className="mt-6 h-10 w-full" />
          ) : (
            <AmountInput
              value={amount}
              onChange={setAmount}
              disabled={!!paid || paymentDetailsLoading}
              className="mt-6"
            />
          )}
          {hasPrompts && hasSlots && (
            <Select
              disabled={paymentDetailsLoading}
              value={slot || prompts.slot[0]}
              onValueChange={(value) => setSlot(value)}
            >
              <SelectTrigger className="mt-4 flex flex-row border-[1px] border-[#CBD5E1] focus:border-[#35D8B7] rounded-md">
                <SelectValue placeholder={translate('SelectMessage')} />
              </SelectTrigger>
              <SelectContent>
                {prompts.slot.map((slotOption) => (
                  <SelectItem key={slotOption} value={slotOption}>
                    {slotOption}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {hasPrompts && !hasSlots && (
            <Input
              type="text"
              inputMode="text"
              value={slot || ''}
              onChange={(e) => setSlot(e.target.value)}
              placeholder={translate('Message')}
              disabled={paymentDetailsLoading}
            />
          )}
          {paid === 0 && (
            <Dialog
              open={isModalOpen}
              onOpenChange={setIsModalOpen}
              style={{ height: 'inherit' }}
            >
              <DialogTrigger asChild>
                <NovovilleButton
                  className="mt-6 sm:w-36 w-full"
                  variant="primary"
                  disabled={paid === 1 || paymentDetailsLoading}
                  loading={paymentDetailsLoading && !isModalOpen}
                  onClick={() => {
                    setIsModalOpen(true);
                  }}
                >
                  {translate('MakeAPayment')}
                </NovovilleButton>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px] flex flex-col items-center text-center gap-4 p-6">
                <Typography variant="bold_20" className="text-gray-800">
                  {translate('PaymentConfirmation')}
                </Typography>
                <Typography
                  variant="regular_16"
                  className="text-gray-700"
                  dangerouslySetInnerHTML={{
                    __html: (
                      translate('PaymentConfirmationText') || ''
                    ).replace(
                      '%%amount%%',
                      `<strong>€${(amount / 100).toFixed(2)}</strong>`
                    ),
                  }}
                />

                <div className="flex flex-col w-full gap-3 mt-4">
                  <NovovilleButton
                    variant="primary"
                    className="w-full"
                    loading={paymentDetailsLoading}
                    onClick={() => {
                      setIsModalOpen(false);
                      handlePreparePayment();
                    }}
                  >
                    {translate('YES')}
                  </NovovilleButton>
                  <NovovilleButton
                    variant="secondary"
                    className="w-full border border-gray-300"
                    disabled={paymentDetailsLoading}
                    onClick={() => {
                      setIsModalOpen(false);
                    }}
                  >
                    {translate('CANCEL')}
                  </NovovilleButton>
                </div>
              </DialogContent>
            </Dialog>
          )}
          {paid === 1 && (
            <NovovilleButton
              className="mt-6  w-full"
              variant="primary"
              onClick={() => {
                window.open(invoiceUrl, '_blank');
              }}
            >
              {translate('DownloadInvoice')}
            </NovovilleButton>
          )}
        </div>
      </div>
    </div>
  );
};

export default withNOVOHeader(withRouter(PaymentConfirmation));
